"use client";

import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";

// ===========================
// Types
// ===========================
interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface AuthContextType {
  accessToken: string | null;
  isLoggedIn: boolean;
  user: User | null;
  login: (response: AuthResponse, redirectUrl?: string | null) => void;
  logout: () => void;
  isAuthChecked: boolean;
}

// ===========================
// Create Context
// ===========================
export const AuthContext = createContext<AuthContextType | null>(null);

// ===========================
// Provider
// ===========================
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContextProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false); // Initially false
  const [isAuthChecked, setIsAuthChecked] = useState<boolean>(false); // New flag
  const router = useRouter();

  useEffect(() => {
    const token = Cookies.get("accessToken");
    const userDetails = Cookies.get("UserDetails");

    if (token) {
      setAccessToken(token);
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }

    if (userDetails) {
      try {
        setUser(JSON.parse(userDetails));
      } catch {
        setUser(null);
      }
    }

    setIsAuthChecked(true); // Mark auth as checked
  }, []);

  const login = (response: AuthResponse, redirectUrl?: string | null) => {
    if (response?.accessToken) {
      setAccessToken(response.accessToken);
      setUser(response.user);
      setIsLoggedIn(true);

      Cookies.set("accessToken", response.accessToken, { expires: 7 });
      Cookies.set("UserDetails", JSON.stringify(response.user), { expires: 7 });

      if (redirectUrl !== undefined) {
        if (redirectUrl) {
          router.replace(redirectUrl);
        }
      } else {
        router.replace("/");
      }
    }
  };

  const logout = () => {
    setAccessToken(null);
    setUser(null);
    setIsLoggedIn(false);

    Cookies.remove("accessToken");
    Cookies.remove("UserDetails");

    localStorage.clear();
    // localStorage.removeItem("cartItems");
    sessionStorage.clear();

    router.replace("/");
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        isLoggedIn,
        user,
        login,
        logout,
        isAuthChecked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ===========================
// Custom hook
// ===========================
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
}
